import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import { worksByType } from "@/mocks";
import type { Work, WorkType } from "@/mocks/works";

const GENRES = ["全部", "剧情", "家庭", "都市", "情感", "悬疑", "古装", "战争", "历史", "年代", "文艺", "青春", "职场", "喜剧"];
const REGIONS = ["全部", "中国大陆", "中国香港", "中国台湾", "日本", "韩国"];
const YEARS = ["全部", "2025", "2024", "2023", "2022", "2021"];
const SORTS = [
  { key: "latest", label: "最新上映" },
  { key: "popular", label: "热门" },
  { key: "rating", label: "人气评分" },
];
const PAGE_SIZE = 6;

export default function Category() {
  const [params, setParams] = useSearchParams();
  const typeParam = (params.get("type") || "all") as WorkType | "all";

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [typeParam, genre, region, year, sort]);

  const typeLabel = typeParam === "movie" ? "电影分类" : typeParam === "tv" ? "电视剧分类" : "全部影视分类";

  const filtered = useMemo<Work[]>(() => {
    let list = worksByType(typeParam);
    if (genre !== "全部") list = list.filter((w) => w.genres.includes(genre));
    if (region !== "全部") list = list.filter((w) => w.region === region);
    if (year !== "全部") list = list.filter((w) => String(w.year) === year);
    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => b.year - a.year || b.views - a.views);
    if (sort === "popular") sorted.sort((a, b) => b.views - a.views);
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    return sorted;
  }, [typeParam, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  };

  const setType = (type: WorkType | "all") => {
    const next = new URLSearchParams(params);
    if (type === "all") next.delete("type");
    else next.set("type", type);
    setParams(next, { replace: true });
    resetFilters();
  };

  const activeFilterCount = [genre, region, year].filter((v) => v !== "全部").length + (sort !== "latest" ? 1 : 0);

  const filterGroupClass = "flex flex-col gap-2 border-b border-background-200/70 py-3.5 first:pt-0 last:border-b-0 last:pb-0 md:flex-row md:items-center md:gap-4";

  const renderFilterGroup = ({ title, options, value, onChange }: { title: string; options: string[]; value: string; onChange: (v: string) => void }) => (
    <div className={filterGroupClass}>
      <span className="w-16 shrink-0 text-xs font-medium text-foreground-500">{title}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
              value === opt
                ? "bg-primary-500 font-medium text-background-50"
                : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-6 md:px-6 md:py-9">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类", to: "/category" }, { label: typeLabel }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{typeLabel}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            根据类型、地区与年份筛选影视作品，查看每部作品的完整剧情简介与相关资讯。当前共收录
            <strong className="mx-1 font-medium text-primary-700">{filtered.length}</strong>
            部作品。
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {[
              { key: "all" as const, label: "全部影视", icon: "ri-apps-2-line" },
              { key: "movie" as const, label: "电影", icon: "ri-film-line" },
              { key: "tv" as const, label: "电视剧", icon: "ri-tv-2-line" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setType(tab.key)}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  typeParam === tab.key
                    ? "bg-primary-500 font-medium text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={`${tab.icon} text-base leading-none`} />
                </span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-6 md:py-8">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-6 px-4 md:px-6 lg:grid-cols-12">
          {/* 筛选栏 - 桌面 */}
          <aside className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200/80 bg-background-100 p-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-sm font-bold text-foreground-950">筛选条件</h2>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="cursor-pointer whitespace-nowrap text-xs text-primary-600 hover:text-primary-700"
                  >
                    重置
                  </button>
                )}
              </div>
              <div className="mt-2">
                {renderFilterGroup({ title: "类型", options: GENRES, value: genre, onChange: setGenre })}
                {renderFilterGroup({ title: "地区", options: REGIONS, value: region, onChange: setRegion })}
                {renderFilterGroup({ title: "年份", options: YEARS, value: year, onChange: setYear })}
                <div className="flex flex-col gap-2 border-t border-background-200/70 pt-3.5 md:flex-row md:items-center md:gap-4">
                  <span className="w-16 shrink-0 text-xs font-medium text-foreground-500">排序</span>
                  <div className="flex flex-wrap gap-1.5">
                    {SORTS.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setSort(s.key)}
                        className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                          sort === s.key
                            ? "bg-accent-500 font-medium text-background-50"
                            : "text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9">
            {/* 移动端筛选入口 */}
            <div className="flex items-center justify-between gap-3 lg:hidden">
              <p className="text-xs text-foreground-600">
                共 <span className="font-medium text-foreground-950">{filtered.length}</span> 部作品
              </p>
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-800"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-filter-3-line text-base leading-none" />
                </span>
                筛选
                {activeFilterCount > 0 && (
                  <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-background-50">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {paged.length > 0 ? (
              <>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-2 md:gap-5 lg:mt-0 lg:grid-cols-3">
                  {paged.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPage(Math.max(1, safePage - 1))}
                      disabled={safePage === 1}
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="上一页"
                    >
                      <i className="ri-arrow-left-s-line text-lg leading-none" />
                    </button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPage(i + 1)}
                        className={`h-9 min-w-9 cursor-pointer rounded-md px-2 text-sm transition-colors ${
                          safePage === i + 1
                            ? "bg-primary-500 font-medium text-background-50"
                            : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                      disabled={safePage === totalPages}
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="下一页"
                    >
                      <i className="ri-arrow-right-s-line text-lg leading-none" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-background-200/80 bg-background-100 px-4 py-16 text-center lg:mt-0">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-primary-500">
                  <i className="ri-search-eye-line text-2xl leading-none" />
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">抱歉，暂无符合条件的影视作品</h2>
                <p className="mt-2 text-sm text-foreground-600">试着放宽筛选条件，或浏览我们的全部影视作品。</p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置筛选
                  </button>
                  <Link
                    to="/"
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 移动端筛选弹层 */}
      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setFilterOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-xl bg-background-50 p-5 animate-fade-up">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-lg leading-none" />
              </button>
            </div>
            <div className="mt-2">
              {renderFilterGroup({ title: "类型", options: GENRES, value: genre, onChange: setGenre })}
              {renderFilterGroup({ title: "地区", options: REGIONS, value: region, onChange: setRegion })}
              {renderFilterGroup({ title: "年份", options: YEARS, value: year, onChange: setYear })}
              <div className="flex flex-col gap-2 border-t border-background-200/70 pt-3.5">
                <span className="text-xs font-medium text-foreground-500">排序</span>
                <div className="flex flex-wrap gap-1.5">
                  {SORTS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setSort(s.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-xs transition-colors ${
                        sort === s.key ? "bg-accent-500 font-medium text-background-50" : "bg-background-100 text-foreground-700"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 py-2.5 text-sm font-medium text-foreground-800"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-semibold text-background-50"
              >
                查看 {filtered.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}